import {
  MateSolveStatus,
  type AssemblyMateControllerState,
} from '@puppycad/cad-core';

/**
 * Mate 诊断展示类型。
 */
export type AssemblyMateDiagnosticType = 'error' | 'warning';

/**
 * 可用于展示诊断的 Mate 状态快照。
 */
type AssemblyMateDiagnosticState = Pick<AssemblyMateControllerState, 'solveStatus' | 'errorMessage'>;

/**
 * 读取字符串形式的 Mate 错误。
 *
 * @param value cad-core 输出的错误字段。
 * @returns 可展示的错误文案；无有效文案时返回 null。
 */
function readAssemblyMateErrorMessage(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }
  return value;
}

/**
 * 解析新建草稿阶段的通用未解文案。
 *
 * @returns 草稿未解诊断。
 */
function resolveAssemblyMateDraftUnsolvedText(): string {
  return '无法求解当前配合。请检查配合类型、对齐方向和尺寸。';
}

/**
 * 根据 cad-core 标准配合状态解析用户可读诊断。
 *
 * 设计说明：
 * web-client 只消费 cad-core 已归一化的 `solveStatus/errorMessage`，不再通过正则识别
 * “过定义/over-defined”等业务语义。新建草稿与已提交 Mate 的诊断归因统一由 cad-core 输出。
 *
 * @param state cad-core Mate 控制器状态快照。
 * @returns 用户可读诊断文案；无须展示时返回 null。
 */
export function resolveAssemblyMateDiagnosticText(
  state: AssemblyMateDiagnosticState,
): string | null {
  const directError = readAssemblyMateErrorMessage(state.errorMessage);
  if (directError) {
    return directError;
  }
  if (state.solveStatus === MateSolveStatus.OverDefined) {
    return '配合过定义。请检查已选实体和现有配合。';
  }
  if (state.solveStatus === MateSolveStatus.MissingReference) {
    return '配合引用缺失。请检查被配合实体是否仍存在。';
  }
  if (state.solveStatus === MateSolveStatus.UnsupportedGeometry) {
    return '当前几何暂不支持该标准配合。';
  }
  if (state.solveStatus === MateSolveStatus.Unsolved) {
    return resolveAssemblyMateDraftUnsolvedText();
  }
  return null;
}

/**
 * 解析 Mate 顶部确认动作失败时的提示文案。
 *
 * 设计说明：
 * PropertyManager header 可以展示 cad-core 输出的完整诊断；但顶部确认动作属于“当前草稿是否能提交”的即时反馈。
 * 当 cad-core 返回 `Unsolved` 时，即使旧文档诊断里残留 over-defined 文案，也不能把文档级过定义弹到顶部。
 *
 * @param state cad-core Mate 控制器状态快照。
 * @returns 顶部确认失败提示文案；无须提示时返回 null。
 */
export function resolveAssemblyMateSubmitFailureText(
  state: AssemblyMateDiagnosticState,
): string | null {
  if (state.solveStatus === MateSolveStatus.Unsolved) {
    return resolveAssemblyMateDraftUnsolvedText();
  }
  return resolveAssemblyMateDiagnosticText(state);
}

/**
 * 根据 Mate 求解状态解析诊断展示类型。
 *
 * @param status cad-core 标准配合求解状态。
 * @returns Naive UI alert 可消费的展示类型。
 */
export function resolveAssemblyMateDiagnosticType(
  status: MateSolveStatus,
): AssemblyMateDiagnosticType {
  return status === MateSolveStatus.UnsupportedGeometry ? 'warning' : 'error';
}
